import * as React from 'react';
import CheckIcon from '@mui/icons-material/Check';
import { styled } from '@mui/material/styles';

const SELECTED_GREEN = '#3D9970';

type CardCursor = 'default' | 'pointer';

type CardFrameProps = {
    isSelected: boolean;
    cursor: CardCursor;
};

const CardFrame = styled('figure', {
    shouldForwardProp: (prop) => prop !== 'isSelected' && prop !== 'cursor'
})<CardFrameProps>(({ isSelected, cursor }) => ({
    position: 'relative',
    margin: 0,
    padding: '4px 4px 40px',
    backgroundColor: '#fff',
    border: isSelected ? `2px solid ${SELECTED_GREEN}` : '1px solid #ddd',
    borderRadius: '4px',
    boxShadow: '0 1px 4px rgba(0, 0, 0, 0.12), 0 1px 3px rgba(0, 0, 0, 0.08)',
    cursor,
    userSelect: 'none',
    transition: 'border-color 0.15s ease'
}));

const SelectionCheck = styled('span')({
    position: 'absolute',
    top: '-5px',
    left: '-10px',
    zIndex: 100,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '34px',
    height: '34px',
    borderRadius: '17px',
    color: '#fff',
    backgroundColor: 'green',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3)'
});

type ThumbnailCardProps = {
    isSelecting: boolean;
    isSelected: boolean;
    isShiftPressed?: boolean;
    onToggleSelect: () => void;
    onStartSelection?: () => void;
    onOpen?: () => void;
    children: React.ReactNode;
};

type CursorArgs = {
    isSelecting: boolean;
    isShiftPressed: boolean;
    canOpen: boolean;
    canStartSelection: boolean;
};

function getCardCursor({
    isSelecting,
    isShiftPressed,
    canOpen,
    canStartSelection
}: CursorArgs): CardCursor {
    if (isSelecting) {
        return 'pointer';
    }

    if (canOpen) {
        return 'pointer';
    }

    if (isShiftPressed && canStartSelection) {
        return 'pointer';
    }

    return 'default';
}

export function ThumbnailCard({
    isSelecting,
    isSelected,
    isShiftPressed = false,
    onToggleSelect,
    onStartSelection,
    onOpen,
    children
}: ThumbnailCardProps) {
    let cursor = getCardCursor({
        isSelecting,
        isShiftPressed,
        canOpen: onOpen !== undefined,
        canStartSelection: onStartSelection !== undefined
    });

    function handleMouseDown(event: React.MouseEvent) {
        if (event.shiftKey) {
            event.preventDefault();
        }
    }

    function handleClick(event: React.MouseEvent) {
        if (isSelecting) {
            onToggleSelect();
            return;
        }

        if (event.shiftKey && onStartSelection) {
            onStartSelection();
            onToggleSelect();
            return;
        }

        if (onOpen) {
            onOpen();
        }
    }

    return (
        <CardFrame
            isSelected={isSelected}
            cursor={cursor}
            aria-selected={isSelecting ? isSelected : undefined}
            onMouseDown={handleMouseDown}
            onClick={handleClick}
        >
            {isSelected && (
                <SelectionCheck data-testid="thumbnail-selected">
                    <CheckIcon fontSize="small" />
                </SelectionCheck>
            )}
            {children}
        </CardFrame>
    );
}
